/**
 * Talking to the kernel: auth, then reads.
 *
 * The auth half is the shape LO-15 proved in a browser — authorization code
 * with PKCE, access token in sessionStorage. Nothing here is new; it is here
 * because a second app hand-rolling the same flow slightly differently is how
 * two apps end up with two bugs.
 *
 * This is the **host** half — an app signing a *person* in, against its own
 * Cognito app client. A micro-app mounted inside such a host does not use it:
 * it signs in as *itself*, which is `@lopie/kernel-ui/remote-auth`. Borrowing
 * a host's token means inheriting the host's manifest, and the console's is
 * the widest in the system.
 */

// ---------------------------------------------------------------------------
// Where the environment facts come from
//
// `window.KERNEL_CONFIG`, written by a `config.js` outside the bundle, so one
// build artifact moves between environments by swapping a file. Read through
// a function rather than captured at module scope: a package is imported
// before an app's own code runs, and a top-level `window.KERNEL_CONFIG` read
// binds `undefined` forever if the script tag happens to load second. That is
// a failure whose only symptom is `Cannot read properties of undefined`
// pointing at a line in a dependency.
//
// `configure()` exists for an app that would rather pass the facts in — a
// test, or a host that already knows them — and is not the normal path.
// ---------------------------------------------------------------------------

let injected = null;

/** Supply config explicitly instead of reading `window.KERNEL_CONFIG`. */
export function configure(config) {
  injected = config;
}

/** The resolved config, for the host half of the remote contract. Exported
 *  so `host.js` reads the same object rather than keeping a second reader. */
export function hostConfig() {
  return conf();
}

function conf() {
  const c = injected || (typeof window !== "undefined" ? window.KERNEL_CONFIG : null);
  if (!c) {
    throw new Error(
      "no kernel config: load a config.js that sets window.KERNEL_CONFIG, or " +
        "call configure() before using the client",
    );
  }
  return c;
}

// ---------------------------------------------------------------------------
// PKCE
//
// The authorization code comes back through the address bar, where anything on
// the machine can read it. The verifier proves we are the client that started
// the flow, so an intercepted code is not enough to get a token.
// ---------------------------------------------------------------------------

const b64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

async function challengeFor(verifier) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return b64url(digest);
}

export async function signIn() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  sessionStorage.setItem("kernel.pkce", verifier);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: conf().clientId,
    redirect_uri: conf().redirect,
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: await challengeFor(verifier),
  });
  window.location = `${conf().login}/oauth2/authorize?${params}`;
}

// ---------------------------------------------------------------------------
// Staying signed in
//
// The access token lives an hour and the refresh token 30 days, so a person
// signs in once a month rather than every morning. The refresh token is kept
// in localStorage — it has to outlive the tab to do that job — keyed by
// client, and it is revoked at Cognito on sign-out rather than only
// forgotten here. The access token stays in sessionStorage: it is the thing a
// request carries, and an hour is what a stolen one is worth.
//
// Apps hold the token they were given at sign-in. Rather than make every one
// of them re-read it, the request helpers below swap a stale session token
// for the current one (`currentBearer`), refreshing first if it is about to
// expire. An app that builds its own requests can call `freshToken()`, or
// listen for the `kernel:token` event.
// ---------------------------------------------------------------------------

const refreshKey = () => `kernel.refresh.${conf().clientId}`;

function readRefresh() {
  try {
    return localStorage.getItem(refreshKey()) || "";
  } catch {
    return "";
  }
}

function writeRefresh(value) {
  try {
    if (value) localStorage.setItem(refreshKey(), value);
    else localStorage.removeItem(refreshKey());
  } catch {
    // Storage refused (a private window, a policy). The session still works;
    // it just ends with the tab, which is how it behaved before this existed.
  }
}

const EARLY_MS = 60_000;

function expiresSoon(token) {
  const { exp } = claimsOf(token);
  return !exp || exp * 1000 - EARLY_MS < Date.now();
}

let refreshing = null;

async function refresh() {
  const refreshToken = readRefresh();
  if (!refreshToken) return null;
  // One in flight at a time: a screen firing six requests as the token turns
  // over should cause one refresh, not six.
  refreshing ||= (async () => {
    try {
      const response = await fetch(`${conf().login}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          client_id: conf().clientId,
          refresh_token: refreshToken,
        }),
      });
      if (!response.ok) {
        // Expired, revoked, or the password changed. Forgotten, so the next
        // load goes to the sign-in page rather than retrying a dead token.
        if (response.status === 400) writeRefresh("");
        return null;
      }
      const body = await response.json();
      sessionStorage.setItem("kernel.token", body.access_token);
      if (body.refresh_token) writeRefresh(body.refresh_token);
      window.dispatchEvent(new CustomEvent("kernel:token", { detail: body.access_token }));
      return body.access_token;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** A token good for at least another minute, refreshing if needed; null if
 *  this session cannot be renewed and the person has to sign in again. */
export async function freshToken() {
  const token = sessionStorage.getItem("kernel.token");
  if (token && !expiresSoon(token)) return token;
  return refresh();
}

/**
 * The bearer to send for a token an app handed a request helper.
 *
 * Only this app's own session token is swapped: one issued to this client.
 * A token from anywhere else — a remote signed in as itself, a server-side
 * bearer — is sent exactly as given, because substituting the host's session
 * for it would be lending the host's manifest to somebody else's request.
 */
async function currentBearer(token) {
  if (typeof window === "undefined" || !token) return token;
  let clientId;
  try {
    clientId = conf().clientId;
  } catch {
    return token;
  }
  if (claimsOf(token).client_id !== clientId || !expiresSoon(token)) return token;
  return (await freshToken()) || token;
}

export function signOut() {
  sessionStorage.removeItem("kernel.token");
  const refreshToken = readRefresh();
  writeRefresh("");
  if (refreshToken) {
    // Best effort, and not awaited: the redirect below is the sign-out a
    // person sees, and a revoke that failed leaves a token nothing here holds.
    fetch(`${conf().login}/oauth2/revoke`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ token: refreshToken, client_id: conf().clientId }),
    }).catch(() => {});
  }
  const params = new URLSearchParams({
    client_id: conf().clientId,
    // Comes back with a marker so the app knows this was deliberate. Without
    // it the auto-redirect sends you straight back to the login page and the
    // sign-out button appears to do nothing.
    logout_uri: `${conf().redirect}?signedout=1`,
  });
  window.location = `${conf().login}/logout?${params}`;
}

async function exchange(code) {
  const verifier = sessionStorage.getItem("kernel.pkce") || "";
  sessionStorage.removeItem("kernel.pkce");

  const response = await fetch(`${conf().login}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: conf().clientId,
      code,
      redirect_uri: conf().redirect,
      code_verifier: verifier,
    }),
  });
  if (!response.ok) {
    throw new Error(`sign-in failed: ${response.status} ${await response.text()}`);
  }
  const body = await response.json();
  // The access token, not the id token. API Gateway matches its audience list
  // against `client_id`, which only the access token carries, and
  // `cognito:groups` rides on it too.
  sessionStorage.setItem("kernel.token", body.access_token);
  writeRefresh(body.refresh_token || "");
  return body.access_token;
}

export function claimsOf(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part + "=".repeat((4 - (part.length % 4)) % 4)));
  } catch {
    return {};
  }
}

/** Resolve the session on load: finish a redirect, or restore a live token. */
export async function resume() {
  const url = new URL(window.location);
  const code = url.searchParams.get("code");

  if (code) {
    const token = await exchange(code);
    // A spent code left in the address bar means a refresh tries to spend it
    // again and fails in a way that looks like the app is broken.
    window.history.replaceState({}, "", url.pathname);
    return { token, claims: claimsOf(token) };
  }

  // A live access token, or a new one from the refresh token — which is what
  // makes a new tab or the next morning not a trip through the sign-in page.
  const token = await freshToken();
  if (!token) {
    sessionStorage.removeItem("kernel.token");
    return null;
  }
  return { token, claims: claimsOf(token) };
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * A refusal is information, so it survives as structure rather than a string.
 *
 * `not_in_manifest` naming a type and tag means the app was never allowed to
 * ask. An empty page means the user cannot see anything. Those are different
 * sentences and a UI that renders both as "no results" has thrown away the
 * only part a reader could act on.
 */
export class KernelError extends Error {
  constructor(status, body, fallback) {
    super(body?.detail || body?.error || fallback);
    this.status = status;
    this.body = body || {};
    this.error = body?.error;
    this.parameter = body?.parameter;
  }

  /** True when the *app's* manifest is what refused this, not the user's
   *  permissions. The two want different sentences on screen. */
  get isManifest() {
    return this.error === "not_in_manifest" || this.error === "no_manifest";
  }
}

/**
 * The control plane is a second API, and it is not the kernel.
 *
 * `control.lopie.one` answers what Lopie publishes and what this org has
 * installed — a catalog and a set of deployments. The kernel answers what an
 * app may touch and what is in the graph. Two questions, two services, and
 * one of them can create infrastructure in another AWS account.
 *
 * The **token is the same one**, which is worth stating because it looks like
 * it should not be: same pool, same sign-in, and the control plane's
 * authorizer lists exactly one audience — the console's client. So a micro-app
 * token gets a 401 there even though it works perfectly against the kernel,
 * which is the intended shape rather than a gap.
 *
 * Separate functions rather than a `base` argument on `kernel()` because the
 * two are not interchangeable. A screen reaching for the wrong one should
 * fail at the import, not at runtime against an endpoint that happens to 404.
 */
function controlBase() {
  const base = conf().control;
  if (!base) {
    throw new Error(
      "no control-plane URL in the kernel config: this build cannot reach " +
        "control.lopie.one, so the app library is unavailable",
    );
  }
  return base;
}

// ---------------------------------------------------------------------------
// Transports: where a request goes, and what authenticates it
//
// Every helper below takes either a **token** or a **transport**.
//
// A token is the shape every app has used since LO-15 and it is unchanged:
// requests go to `KERNEL_CONFIG.api` / `.control` with the token as a bearer
// header. Nothing about that path moved.
//
// A transport is an object, and exists for a host that does not hold the
// token in the browser at all. `multicloud-console` keeps the session in an
// httpOnly cookie on its own server: in the browser it calls its own origin
// (`browserTransport()`), which forwards with the bearer attached; on the
// server, during a render, it calls the kernel directly with the session's
// token (`bearerTransport()`). One set of helpers for both, so the refusal
// shapes a screen renders are the same object whichever side fetched them.
//
// What a transport does **not** change is what is asked or what is allowed.
// The kernel decides that from the token, wherever the request came from.
// ---------------------------------------------------------------------------

/** Same-origin transport for a host whose server holds the session. */
export function browserTransport() {
  const origin = window.location.origin;
  return {
    api: `${origin}/api`,
    control: `${origin}/control`,
    // A header a cross-site form cannot set. The host's proxy refuses a write
    // without it, which is what stops a cookie-authenticated POST from being
    // made on somebody's behalf by another page.
    headers: { "X-Requested-With": "kernel" },
    credentials: "same-origin",
  };
}

/** Bearer transport with explicit bases, for server-side rendering. */
export function bearerTransport({ token, api, control }) {
  return { api, control, headers: { Authorization: `Bearer ${token}` } };
}

async function resolveTransport(tokenOrTransport, which) {
  const t = tokenOrTransport;
  if (t && typeof t === "object") {
    const base = t[which];
    if (!base) {
      throw new Error(
        which === "control"
          ? "no control-plane URL in this transport: the app library is unavailable"
          : "no kernel API URL in this transport",
      );
    }
    return { base, headers: t.headers || {}, credentials: t.credentials };
  }
  return {
    base: which === "control" ? controlBase() : conf().api,
    headers: { Authorization: `Bearer ${await currentBearer(t)}` },
    credentials: undefined,
  };
}

function init({ headers, credentials }, extra) {
  const out = { ...extra, headers: { ...headers, ...(extra.headers || {}) } };
  if (credentials) out.credentials = credentials;
  return out;
}

export function controlGet(token) {
  return async function get(path) {
    const t = await resolveTransport(token, "control");
    const response = await fetch(t.base + path, init(t, {
      cache: "no-store",
    }));
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

export function controlPost(token) {
  return async function post(path, body) {
    const t = await resolveTransport(token, "control");
    const response = await fetch(t.base + path, init(t, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      body: JSON.stringify(body || {}),
    }));
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      // The control plane's errors name a `field`, which the kernel's do not.
      // Carried through as-is so a form can put the message under the input
      // that caused it rather than at the top of the page.
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

export function kernelPost(token) {
  return async function post(path, body) {
    const t = await resolveTransport(token, "api");
    const response = await fetch(t.base + path, init(t, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      body: JSON.stringify(body || {}),
    }));
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

/**
 * `DELETE`, which this API serves for exactly one thing.
 *
 * Its own function rather than a `method` option on `kernelPost`, because the
 * two are different acts with different grants — `user.set` and `user.remove`
 * are separate actions for the same reason `access.grant` and `access.revoke`
 * are. A screen that could delete by passing an option to the write helper
 * would make "can this code remove somebody" a question about an argument
 * rather than about which function was imported.
 *
 * No body. A DELETE that carried one would invite somebody to put a filter in
 * it, and a bulk delete is not a thing this client should make easy.
 */
export function kernelDelete(token) {
  return async function remove(path) {
    const t = await resolveTransport(token, "api");
    const response = await fetch(t.base + path, init(t, {
      method: "DELETE",
      cache: "no-store",
    }));
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      throw new KernelError(response.status, parsed, text || response.statusText);
    }
    return parsed;
  };
}

export function kernel(token) {
  return async function get(path, params) {
    const t = await resolveTransport(token, "api");
    const url = new URL(t.base + path);
    for (const [key, value] of Object.entries(params || {})) {
      if (value !== "" && value != null) url.searchParams.set(key, value);
    }

    let response;
    try {
      response = await fetch(url, init(t, {
        // The API sends `cache-control: no-store` and this says the same
        // thing from the other end. Belt and braces on purpose: a screen
        // about who can see what, served from a cache, is the one kind of
        // stale this product cannot afford.
        cache: "no-store",
      }));
    } catch (cause) {
      // Browsers say "Failed to fetch" for CORS, DNS and offline alike. It is
      // worth saying which one is likely rather than passing that on.
      throw new KernelError(0, null, `cannot reach ${t.base} — network or CORS`);
    }

    const text = await response.text();
    let body;
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }

    if (!response.ok) {
      throw new KernelError(response.status, body, text || response.statusText);
    }
    // A 200 that is not JSON is a failure, and it used to be returned as
    // `null`. Every caller then read a field off it and the console
    // white-screened with a `TypeError` and no sentence — which is the worst
    // possible version of a refusal, since a blank page tells the reader
    // nothing at all about whose decision produced it.
    //
    // It is not hypothetical: it happened while driving the console in a
    // browser, when an unrelated dev server on the same port answered
    // `/schema` with an HTML page and the whole app went blank. A proxy, a
    // captive portal or a CloudFront error page all do the same thing, and
    // none of them is a 4xx.
    if (body === null && text.trim() !== "") {
      throw new KernelError(response.status, null, (
        `${t.base}${path} answered ${response.status} with something that ` +
        "is not JSON. That is not a permission decision — something between " +
        "this page and the kernel answered instead of the kernel."
      ));
    }
    return body;
  };
}
